import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

/**
 * The app's route table.
 *
 * Home and an open chat are the SAME screen — only the URL differs — so both paths
 * render the same `screen` element. That is deliberate and load-bearing: React sees
 * one component type in one position and keeps the instance across the change, so
 * writing a new chat's id into the URL the moment the server returns it does NOT
 * remount and drop the turn that is still streaming. (react-router renders each
 * match without a key, so reconciliation is by type+position — see AppRoutes.stories.)
 *
 * `/overview` renders the same element for the same reason, one step further: the
 * Insights screen shares the whole frame (left nav, chats list, profile) with chat
 * and only swaps the canvas, so routing it separately would mean a second copy of
 * that wiring — and leaving the chat to read an insight would drop a streaming
 * turn. HomePage decides which canvas to draw from the path.
 *
 * `screen` is a prop only so the story can swap in a probe; the app always passes
 * HomePage. Deliberately NOT routed: the login gate (a signed-out deep link keeps
 * its URL, so sign-in returns to it) and the result panel's OPEN TABS (a per-device
 * work layout in localStorage — the visible one is `?tab=`, which is a location).
 *
 * `insights` is passed in rather than read here: it comes from the signed-in
 * tenant's token, and App — which already holds the session — is the one place that
 * reads the claim. That keeps this a pure route table, so a story can exercise the
 * feature ON and OFF instead of only whatever the build was compiled with.
 *
 */
export function AppRoutes({
  screen,
  insights = false,
  impersonation = false,
  attackSurface = false,
}: {
  screen: ReactNode;
  insights?: boolean;
  /** The user holds the `Brand Impersonation` permission. */
  impersonation?: boolean;
  /** The user holds the `Attack Surface` permission. */
  attackSurface?: boolean;
}) {
  return (
    <Routes>
      <Route path="/" element={screen} />
      <Route path="/chat/:sessionId" element={screen} />
      {/* Integrations and Settings ride in the same screen's canvas: the shell
          (left nav + its chats/projects wiring) lives in HomePage and there is
          exactly one of it. ponytail: still one `element` per path and a 3-way
          switch in HomePage — extract a layout route with an <Outlet> when the
          shell itself has to differ per screen, not merely for a fourth path.
          `/integrations/<slug>` opens one integration — and unlike a chat this
          content is the same for everyone, so that link really is shareable. */}
      <Route path="/integrations" element={screen} />
      <Route path="/integrations/:slug" element={screen} />
      {/* Feeds rides the same shell: a table of the tenant's lists plus the URL
          builder for the selected one. Read-only, so there is no sub-path — the
          selected row is state, not a location (nothing to share). */}
      <Route path="/feeds" element={screen} />
      {/* Brand impersonation: the tenant's lookalike domains and the decision
          recorded against each. Two locations — the list and one domain's page,
          which is a thing you send someone a link to (as a finding is on Attack
          surface below); the picked filters are state. Gated on the permission:
          absent → `*` sends a pasted link home. */}
      {impersonation && <Route path="/brand-impersonation" element={screen} />}
      {impersonation && <Route path="/brand-impersonation/domains/:domain" element={screen} />}
      {/* Attack surface: the tenant's external surface and the findings on it.
          Three locations — the Findings tab, the Assets tab, one finding — because
          each is a thing you send someone a link to. Gated on the permission
          alone (the server gates the same way); absent → `*` sends a pasted link
          home. Rides in the result panel as a pinned tab like Brand impersonation. */}
      {attackSurface && <Route path="/attack-surface" element={screen} />}
      {attackSurface && <Route path="/attack-surface/assets" element={screen} />}
      {attackSurface && <Route path="/attack-surface/findings/:findingId" element={screen} />}
      {/* API docs: the public API's OpenAPI document rendered on our DS. Same
          reasoning as Feeds — the selected operation is state, not a location. */}
      <Route path="/api-docs" element={screen} />
      {/* User Guide: the product manual rendered in-app. `/guide/<slug>` opens
          one chapter — like an integration, the content is the same for
          everyone, so a chapter link really is shareable. */}
      <Route path="/guide" element={screen} />
      <Route path="/guide/:slug" element={screen} />
      {/* `/settings/<section>` — which panel is open is a location, not state. */}
      <Route path="/settings" element={screen} />
      <Route path="/settings/:section" element={screen} />
      {/* Absent for a tenant without the feature, so a pasted `/overview` falls
          through to `*` below and goes home — rather than rendering a screen that
          tenant has no cards for. */}
      {insights && <Route path="/overview" element={screen} />}
      {/* Anything else → home, replaced so Back doesn't return to the dead URL.
          "Home" is whatever signing in lands on: Overview for an insights tenant,
          the composer otherwise. Without the branch a broken link landed on the
          composer while a clean entry landed on the cards — two different front
          doors for the same person. (LandOnOverview can't cover this: its one
          shot fires while the URL is still the dead path.) */}
      <Route path="*" element={<Navigate to={insights ? '/overview' : '/'} replace />} />
    </Routes>
  );
}
